import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, Loader2, RotateCcw, Eye, Clock, User } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * Read-only version history for a report: list every saved version (number,
 * description, author, timestamp), preview a version's contents read-only, and
 * roll the live report back to any version.
 */
export default function ReportVersionsDialog({ open, onOpenChange, reportId, currentVersion, onRolledBack }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);     // full version doc (preview)
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  useEffect(() => {
    if (!open || !reportId) return;
    setLoading(true);
    setSelected(null);
    api.getReportVersions(reportId)
      .then(v => setVersions(Array.isArray(v) ? v : []))
      .catch(e => toast.error(e.message || 'Could not load versions'))
      .finally(() => setLoading(false));
  }, [open, reportId]);

  const preview = async (ver) => {
    setPreviewLoading(true);
    try { setSelected(await api.getReportVersion(reportId, ver)); }
    catch (e) { toast.error(e.message || 'Could not load version'); }
    finally { setPreviewLoading(false); }
  };

  const rollback = async (ver) => {
    if (!window.confirm(`Roll the report back to version ${ver}? This creates a new version with v${ver}'s content.`)) return;
    setRollingBack(ver);
    try {
      await api.rollbackReport(reportId, ver);
      toast.success(`Rolled back to version ${ver}`);
      onOpenChange(false);
      if (onRolledBack) onRolledBack();
    } catch (e) { toast.error(e.message || 'Rollback failed'); }
    finally { setRollingBack(null); }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleString(); } catch { return d; } };
  const previewVisuals = selected?.config?.visuals || [];
  const previewPages = selected?.config?.pages || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl max-w-3xl" data-testid="report-versions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Version history</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Every save creates a version. Preview any version read-only, or roll back to restore it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-5 gap-3 mt-2" style={{ minHeight: 340 }}>
          {/* Version list */}
          <div className="col-span-2 border-r border-border/40 pr-3 overflow-y-auto" style={{ maxHeight: 420 }} data-testid="versions-list">
            {loading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : versions.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10">No versions yet.</p>
            ) : versions.map(v => (
              <button
                key={v.version}
                onClick={() => preview(v.version)}
                className={`w-full text-left p-2 rounded-lg mb-1.5 border transition-colors ${selected?.version === v.version ? 'border-primary/50 bg-primary/10' : 'border-border/30 hover:border-border/60 hover:bg-muted/30'}`}
                data-testid={`version-row-${v.version}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono font-semibold text-foreground">v{v.version}</span>
                  {v.version === currentVersion && <Badge className="text-[8px] bg-emerald-500/20 text-emerald-300 border-emerald-500/30">current</Badge>}
                </div>
                <p className="text-[11px] text-foreground/90 truncate mt-0.5">{v.description || 'Saved'}</p>
                <div className="flex items-center gap-2 text-[9px] text-muted-foreground/70 mt-0.5">
                  <span className="flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{v.saved_by || '—'}</span>
                  <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{fmtDate(v.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
          {/* Read-only preview */}
          <div className="col-span-3 overflow-y-auto" style={{ maxHeight: 420 }} data-testid="version-preview">
            {previewLoading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : !selected ? (
              <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
                <Eye className="w-6 h-6 mb-2 opacity-50" />
                <p className="text-xs">Select a version to preview it read-only.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{selected.name} <span className="font-mono text-xs text-muted-foreground">v{selected.version}</span></p>
                    <p className="text-[11px] text-muted-foreground">{selected.description || 'Saved'}</p>
                  </div>
                  <Button size="sm" onClick={() => rollback(selected.version)} disabled={rollingBack != null || selected.version === currentVersion} className="rounded-lg gap-1.5" data-testid={`rollback-${selected.version}`}>
                    {rollingBack === selected.version ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    {selected.version === currentVersion ? 'Current' : 'Roll back'}
                  </Button>
                </div>
                <div className="rounded-lg border border-border/40 bg-muted/20 p-2 text-[11px] text-muted-foreground space-y-1" data-testid="version-preview-summary">
                  <div className="flex justify-between"><span>Saved by</span><span className="text-foreground">{selected.saved_by || '—'}</span></div>
                  <div className="flex justify-between"><span>Saved at</span><span className="text-foreground">{fmtDate(selected.created_at)}</span></div>
                  <div className="flex justify-between"><span>Pages</span><span className="text-foreground">{previewPages.length || 1}</span></div>
                  <div className="flex justify-between"><span>Visuals</span><span className="text-foreground">{previewVisuals.length}</span></div>
                </div>
                {previewVisuals.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Visuals in this version</p>
                    <div className="space-y-1">
                      {previewVisuals.slice(0, 20).map((vi, i) => (
                        <div key={vi.id || i} className="flex items-center gap-2 text-[11px] p-1.5 rounded bg-muted/20 border border-border/20" data-testid={`version-visual-${i}`}>
                          <Badge variant="outline" className="text-[8px]">{vi.vizType || 'table'}</Badge>
                          <span className="text-foreground/90 truncate">{vi.title || vi.vizType || `Visual ${i + 1}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
